import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import alertsHelper from "../../../../API/Alerts/alertsHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import AgentBuilderSidebar from "../../../Agents/Components/AgentBuilderSidebar";
import AlertsSettings from "./AlertsSettings";
import {
  OPERATOR_VALUES,
  SEVERITY_VALUES,
  TRIGGER_TYPE_VALUES,
  emptyAlert,
  fromRecord,
} from "./alertUtils";

const HASH_ROUTED_SECTIONS = [
  "overview",
  "conditions",
  "recipients",
  "notifications",
];

const AlertBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const alertId = params?.alertId;
  const isEditMode = !!alertId && alertId !== "new";
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const { getActiveWorkspace, getWorkspaceUsers } = useWorkspaces();

  const [activeSection, setActiveSection] = useState("overview");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  const [saveVersion, setSaveVersion] = useState(0);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const [isAlertsLoading, setIsAlertsLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const editInitRef = useRef(null);

  const workspaceUsers = useMemo(
    () => getWorkspaceUsers?.() || [],
    [getWorkspaceUsers],
  );

  const triggerTypes = useMemo(
    () =>
      TRIGGER_TYPE_VALUES.map((v) => ({
        label: t(`alerts.triggerType${v.charAt(0)}${v.slice(1).toLowerCase()}`),
        value: v,
      })),
    [t],
  );
  const severities = useMemo(
    () =>
      SEVERITY_VALUES.map((v) => ({
        label: t(`alerts.severity${v.charAt(0).toUpperCase()}${v.slice(1)}`),
        value: v,
      })),
    [t],
  );
  const operators = useMemo(() => {
    const labelKey = {
      eq: "opEq",
      neq: "opNeq",
      gt: "opGt",
      gte: "opGte",
      lt: "opLt",
      lte: "opLte",
      contains: "opContains",
      not_contains: "opNotContains",
    };
    return OPERATOR_VALUES.map((v) => ({
      label: t(`alerts.${labelKey[v]}`),
      value: v,
    }));
  }, [t]);

  const loadAlerts = useCallback(async (workspaceID) => {
    try {
      setIsAlertsLoading(true);
      const items = await alertsHelper.getAlertsByWorkspace(workspaceID);
      setAlerts(items || []);
    } catch (error) {
      console.error("Error loading alerts:", error);
      setAlerts([]);
    } finally {
      setIsAlertsLoading(false);
    }
  }, []);

  useEffect(() => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    loadAlerts(wsId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    if (!alertId) {
      history.replace(`/workspaces/${workspaceId}/settings/alerts`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (alertId !== "new") return;
    setInitialMapValues({ ...emptyAlert() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    if (isAlertsLoading) return;
    if (editInitRef.current === alertId) return;
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    const alert = (alerts || []).find((u) => u?.id === alertId);
    if (!alert) {
      const listIsStale = alerts?.some(
        (u) => u?.workspaceID && u?.workspaceID !== activeWorkspaceId,
      );
      if (listIsStale) return;
      history.replace(`/workspaces/${activeWorkspaceId}/settings/alerts`);
      return;
    }
    editInitRef.current = alertId;
    setInitialMapValues(fromRecord(alert, workspaceUsers));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertId, isEditMode, isAlertsLoading, alerts, workspaceUsers]);

  const sectionGroups = useMemo(
    () => [
      {
        id: "setup",
        label: t("jobs.builderGroupSetup", { defaultValue: "Set up" }),
        items: [
          {
            id: "overview",
            title: t("alerts.builderSectionOverview", {
              defaultValue: "Overview",
            }),
            icon: AppAssets.icons.persona,
          },
          {
            id: "conditions",
            title: t("alerts.builderSectionConditions", {
              defaultValue: "Conditions",
            }),
            icon: AppAssets.icons.filter,
          },
          {
            id: "recipients",
            title: t("alerts.builderSectionRecipients", {
              defaultValue: "Recipients",
            }),
            icon: AppAssets.icons.userInfo,
          },
          {
            id: "notifications",
            title: t("alerts.notifications"),
            icon: AppAssets.icons.notification,
          },
        ],
      },
    ],
    [t],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (HASH_ROUTED_SECTIONS.includes(raw)) {
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertId, isEditMode, initialMapValues]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/alerts/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("jobs.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const isFormReady =
    initialMapValues != null &&
    !isAlertsLoading &&
    (!isEditMode || initialMapValues?.id === alertId);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [activeSection]);

  const handleAlertCreated = (status, isEdit, savedId, submittedValues) => {
    if (status === "success") {
      AppToasty.show(t("alerts.saved"), { type: "success" });
      formRef?.current?.resetForm?.({
        values: submittedValues || formRef.current?.values,
      });
      setSaveVersion((v) => v + 1);
      if (!isEdit && savedId) {
        history.replace(`/workspaces/${workspaceId}/alerts/${savedId}`);
      }
    } else {
      AppToasty.show(t("alerts.saveFailed"), { type: "danger" });
    }
  };

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={() => formRef?.current?.submitForm()}
      isDisabled={!isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const alert = useMemo(
    () => (alerts || []).find((u) => u?.id === alertId),
    [alerts, alertId],
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ToothFairyLoadingPlaceholder caption={t("alerts.loadingAlerts")} />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        <AgentBuilderSidebar
          groups={sectionGroups}
          activeSection={activeSection}
          onSelect={setActiveSection}
          errorSections={errorSections}
        />
        <ScrollView
          ref={scrollRef}
          style={{ flex: 1, minWidth: 0 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <AlertsSettings
            key={alertId}
            ref={formRef}
            initialMapValues={initialMapValues}
            isEdit={isEditMode}
            selectedId={isEditMode ? alertId : undefined}
            onAlertCreated={handleAlertCreated}
            onAlertDeleted={() =>
              history.push(`/workspaces/${workspaceId}/settings/alerts`)
            }
            isBuilderLayout
            builderActiveSection={activeSection}
            onBuilderContextChange={setBuilderCtx}
            saveVersion={saveVersion}
            workspaceUsers={workspaceUsers}
            triggerTypes={triggerTypes}
            severities={severities}
            operators={operators}
          />
        </ScrollView>
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(`/workspaces/${workspaceId}/settings/alerts`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("settings.alerts")}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.name || alert?.name || ""
            : t("alerts.createAlert")}
        </AppText>
        <View style={{ flex: 1 }} />
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("jobs.unsavedChangesSubCaption", {
          defaultValue:
            "This job has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default AlertBuilderScreenContainer;
